/**
 * StaffLayout — outer shell for all /app/* staff pages inside the GHL iframe.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────┐
 *   │  Top bar: [ClearIntake] [Staff Name · Location Name]   │
 *   │           Unread badge: 3 messages, 7 new submissions   │
 *   ├────────────────┬────────────────────────────────────────┤
 *   │  Left sidebar  │  Main content area (children)          │
 *   │  📬 Mail (3)   │                                        │
 *   │  📋 Forms (7)  │                                        │
 *   │  ─────────     │                                        │
 *   │  ⚙️ Settings   │                                        │
 *   └────────────────┴────────────────────────────────────────┘
 *
 * The contact widget (/app/contact/:id) does NOT use this layout —
 * it's a compact sidebar embed and renders directly.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth-context.js';
import { useUnreadCounts } from '../../hooks/useUnreadCounts.js';
import { settings as settingsApi } from '../../lib/api.js';
import GettingStartedModal from '../../components/GettingStartedModal.js';

/** localStorage flag (non-PHI UI preference) — set when the user opts out of the
 *  first-run Getting Started guide. */
const GETTING_STARTED_HIDDEN_KEY = 'clearintake:gettingStartedHidden';

function isGettingStartedHidden(): boolean {
  try { return localStorage.getItem(GETTING_STARTED_HIDDEN_KEY) === '1'; }
  catch { return false; } // localStorage may be blocked in a partitioned iframe — just show it
}

interface Props {
  children: React.ReactNode;
}

// Inline SVG icons — tintable via `color`, no emoji OS variance
const IconMail = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);
const IconForms = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M9 9h6M9 13h6M9 17h4"/>
  </svg>
);
const IconSettings = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.07 4.93A10 10 0 0 0 4.93 19.07M4.93 4.93A10 10 0 0 0 19.07 19.07"/>
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2"/>
  </svg>
);
const IconLock = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);
const IconHelp = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

export default function StaffLayout({ children }: Props): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const { messageUnread, formSubmissionNew } = useUnreadCounts();
  const [practiceName,  setPracticeName]  = useState<string>('');
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [isNarrow,      setIsNarrow]      = useState(window.innerWidth < 768);
  // First-run Getting Started guide: auto-open once per load unless the user opted out.
  const [showGuide,     setShowGuide]     = useState<boolean>(() => !isGettingStartedHidden());

  const handleGuideClose = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      try { localStorage.setItem(GETTING_STARTED_HIDDEN_KEY, '1'); } catch { /* iframe/storage blocked — no-op */ }
    }
    setShowGuide(false);
  };

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const staffUser = user?.role === 'staff' ? user : null;

  useEffect(() => {
    settingsApi.get().then((res) => {
      if (res.success && res.data?.practiceName) setPracticeName(res.data.practiceName);
    }).catch(() => { /* silent — location ID is the fallback */ });
  }, []);

  const navItems: Array<{ path: string; icon: React.ReactElement; label: string; badge?: number }> = [
    { path: '/app/mailbox', icon: <IconMail />,   label: 'Mail',  badge: messageUnread    },
    { path: '/app/forms',   icon: <IconForms />,  label: 'Forms', badge: formSubmissionNew },
  ];

  const isActive = (path: string): boolean =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div style={styles.shell}>
      {/* ── Top bar ── */}
      <div style={styles.topBar}>
        <div style={styles.topLeft}>
          {isNarrow && (
            <button
              style={styles.hamburger}
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label={sidebarOpen ? 'Close navigation' : 'Open navigation'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {sidebarOpen
                  ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
                  : <><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></>
                }
              </svg>
            </button>
          )}
          <span style={styles.brandIcon}><IconLock /></span>
          <span style={styles.brand}>ClearIntake</span>
        </div>
        <div style={styles.topRight}>
          {staffUser && (
            <>
              <span style={styles.staffName}>{staffUser.name || staffUser.email}</span>
              {(practiceName || staffUser.locationId) && (
                <>
                  <span style={styles.topDivider}>·</span>
                  <span style={styles.locationName}>{practiceName || staffUser.locationId}</span>
                </>
              )}
            </>
          )}
          {(messageUnread > 0 || formSubmissionNew > 0) && (
            <div style={styles.topBadges}>
              {messageUnread > 0 && (
                <span style={styles.topBadge}>{messageUnread} unread</span>
              )}
              {formSubmissionNew > 0 && (
                <span style={styles.topBadge}>
                  {formSubmissionNew} new
                </span>
              )}
            </div>
          )}
          <button
            style={styles.helpBtn}
            onClick={() => setShowGuide(true)}
            title="Getting Started guide"
            aria-label="Open Getting Started guide"
          >
            <IconHelp />
          </button>
        </div>
      </div>

      {showGuide && <GettingStartedModal onClose={handleGuideClose} />}

      {/* ── Body ── */}
      <div style={styles.body}>
        {/* Sidebar — hidden on narrow viewports unless toggled */}
        {isNarrow && sidebarOpen && (
          <div
            style={styles.sidebarOverlay}
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}
        <nav
          style={{
            ...styles.sidebar,
            ...(isNarrow
              ? { position: 'fixed', top: TOPBAR_H, left: 0, bottom: 0, zIndex: 100,
                  transform: sidebarOpen ? 'translateX(0)' : `translateX(-${SIDEBAR_W}px)`,
                  transition: 'transform 0.2s ease' }
              : {}),
          }}
        >
          <div style={styles.navGroup}>
            {navItems.map((item) => (
              <button
                key={item.path}
                style={{
                  ...styles.navBtn,
                  ...(isActive(item.path) ? styles.navBtnActive : {}),
                }}
                onClick={() => navigate(item.path)}
              >
                <span style={styles.navIcon} aria-hidden="true">{item.icon}</span>
                <span style={styles.navLabel}>{item.label}</span>
                {item.badge != null && item.badge > 0 && (
                  <span style={styles.navBadge}>{item.badge > 99 ? '99+' : item.badge}</span>
                )}
              </button>
            ))}
          </div>

          {isAdmin && (
            <>
              <div style={styles.navDivider} />

              <div style={styles.navGroup}>
                <button
                  style={{
                    ...styles.navBtn,
                    ...(isActive('/app/settings') ? styles.navBtnActive : {}),
                  }}
                  onClick={() => navigate('/app/settings')}
                >
                  <span style={styles.navIcon} aria-hidden="true"><IconSettings /></span>
                  <span style={styles.navLabel}>Settings</span>
                </button>
              </div>
            </>
          )}
        </nav>

        {/* Main content */}
        <main style={styles.main}>
          {children}
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const TOPBAR_H = 52;
const SIDEBAR_W = 160;

const styles: Record<string, React.CSSProperties> = {
  shell: {
    display:       'flex',
    flexDirection: 'column',
    height:        '100vh',
    overflow:      'hidden',
    fontFamily:    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    background:    '#f8fafc',
  },
  // White top bar matching GHL content-area header pattern
  topBar: {
    height:         TOPBAR_H,
    minHeight:      TOPBAR_H,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    padding:        '0 20px',
    background:     '#ffffff',
    borderBottom:   '1px solid #e2e8f0',
    flexShrink:     0,
  },
  topLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  hamburger: {
    display:     'flex',
    alignItems:  'center',
    padding:     '4px',
    border:      'none',
    background:  'none',
    cursor:      'pointer',
    color:       '#374151',
    borderRadius: 4,
  },
  brandIcon: { display: 'flex', alignItems: 'center', color: '#2563eb' },
  brand:   { fontSize: 15, fontWeight: 700, color: '#111827', letterSpacing: '-0.3px' },
  topRight: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 },
  staffName:    { color: '#374151', fontWeight: 500 },
  topDivider:   { color: '#cbd5e1' },
  locationName: { color: '#6b7280' },
  helpBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginLeft: 4, padding: 5, border: 'none', background: 'none',
    color: '#6b7280', cursor: 'pointer', borderRadius: 6,
  },
  topBadges: { display: 'flex', gap: 6, marginLeft: 8 },
  topBadge: {
    background:   '#2563eb',
    color:        '#fff',
    borderRadius: 9999,
    padding:      '2px 8px',
    fontSize:     11,
    fontWeight:   600,
  },
  body: {
    display:  'flex',
    flex:     1,
    overflow: 'hidden',
  },
  sidebar: {
    width:         SIDEBAR_W,
    minWidth:      SIDEBAR_W,
    background:    '#f8fafc',
    borderRight:   '1px solid #e2e8f0',
    display:       'flex',
    flexDirection: 'column',
    padding:       '12px 0',
    overflow:      'hidden',
    flexShrink:    0,
  },
  sidebarOverlay: {
    position:   'fixed',
    inset:      0,
    background: 'rgba(0,0,0,0.3)',
    zIndex:     99,
  },
  navGroup: { display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' },
  navDivider: {
    height:     1,
    background: '#e2e8f0',
    margin:     '8px 12px',
  },
  navBtn: {
    display:      'flex',
    alignItems:   'center',
    gap:          8,
    padding:      '8px 10px',
    border:       'none',
    background:   'none',
    borderRadius: 6,
    cursor:       'pointer',
    fontSize:     13,
    fontWeight:   500,
    color:        '#374151',
    textAlign:    'left',
    width:        '100%',
    position:     'relative',
    fontFamily:   "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    transition:   'background 0.1s',
  },
  navBtnActive: {
    background: '#eff6ff',
    color:      '#2563eb',
  },
  navIcon:  { display: 'flex', alignItems: 'center', lineHeight: 1 },
  navLabel: { flex: 1 },
  navBadge: {
    background:   '#2563eb',
    color:        '#fff',
    borderRadius: 9999,
    padding:      '2px 6px',
    fontSize:     10,
    fontWeight:   700,
    minWidth:     18,
    textAlign:    'center',
  },
  main: {
    flex:          1,
    overflow:      'hidden',
    display:       'flex',
    flexDirection: 'column',
  },
};
